import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ConcertTicket from '../components/ConcertTicket';

function Home() {
  const [lastHoveredConcert, setLastHoveredConcert] = useState(null);

  // Concert ticket data - spells SAMI!
  const concertTickets = [
    {
      title: 'Experiences',
      venue: 'USC Games Expo, Los Angeles',
      subtitle: 'Mini Golf, Escape Rooms & More',
      date: { day: 'FRI', month: 'MAY', date: '12', year: '2026' },
      section: 'S',
      row: '1',
      seat: '01',
      price: 'VIP ACCESS',
      link: '/experiential/immersive',
      rotation: -2,
      delay: 0.2,
      colorTheme: 'coral',
      zIndex: 4,
    },
    {
      title: 'Digital',
      venue: 'Feels HQ, Los Angeles',
      subtitle: 'Apps, Products & Interfaces',
      date: { day: 'SAT', month: 'JAN', date: '08', year: '2026' },
      section: 'A',
      row: '2',
      seat: '02',
      price: 'GUEST LIST',
      link: '/experiential/digital',
      rotation: 1.5,
      delay: 0.35,
      colorTheme: 'dusty',
      zIndex: 3,
    },
    {
      title: 'Concepts',
      venue: 'The Anniversary Pop-Up',
      subtitle: 'Ideas Waiting to Be Built',
      date: { day: 'THU', month: 'JAN', date: '08', year: '2026' },
      section: 'M',
      row: '3',
      seat: '03',
      price: 'COMP',
      link: '/experiential/concepts',
      rotation: -1,
      delay: 0.5,
      colorTheme: 'sage',
      zIndex: 2,
    },
    {
      title: 'About',
      venue: 'Bovard Auditorium, USC',
      subtitle: 'The Person Behind the Work',
      date: { day: 'TUE', month: 'MAY', date: '14', year: '2026' },
      section: 'I',
      row: '4',
      seat: '04',
      price: 'VIP ACCESS',
      link: '/experiential/about',
      rotation: 2,
      delay: 0.65,
      colorTheme: 'golden',
      zIndex: 1,
    },
  ];

  return (
    <motion.div
      className="home-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Editorial Hero Section */}
      <section className="editorial-hero">
        <div className="hero-container">
          {/* Left side - Big Typography */}
          <div className="hero-text-side">
            <motion.span
              className="hero-label"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Experiential Designer
            </motion.span>

            <h1 className="editorial-title">
              <motion.span
                className="title-line title-small"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              >
                I design
              </motion.span>
              <motion.span
                className="title-line title-huge"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                worlds
              </motion.span>
              <motion.span
                className="title-line title-medium"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                people <em>want</em> to
              </motion.span>
              <motion.span
                className="title-line title-accent"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
              >
                step into.
              </motion.span>
            </h1>

            <motion.p
              className="hero-subtext"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              From immersive installations to fan activations to interactive digital moments, I create
              experiences that make people <em>feel</em> part of something.
            </motion.p>
          </div>

          {/* Right side - Concert Tickets */}
          <div className="hero-tickets-side">
            <div className="concert-tickets-grid">
              {concertTickets.map((ticket, index) => (
                <ConcertTicket
                  key={ticket.section}
                  title={ticket.title}
                  venue={ticket.venue}
                  subtitle={ticket.subtitle}
                  date={ticket.date}
                  section={ticket.section}
                  row={ticket.row}
                  seat={ticket.seat}
                  price={ticket.price}
                  link={ticket.link}
                  rotation={ticket.rotation}
                  delay={ticket.delay}
                  colorTheme={ticket.colorTheme}
                  zIndex={lastHoveredConcert === index ? 50 : ticket.zIndex}
                  isHovered={lastHoveredConcert === index}
                  onHoverStart={() => setLastHoveredConcert(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Project - Playful Organic Layout */}
      <section className="featured-project">
        <div className="container">
          <div className="featured-layout">
            {/* Floating label */}
            <motion.div
              className="floating-label"
              initial={{ opacity: 0, rotate: -5 }}
              whileInView={{ opacity: 1, rotate: -3 }}
              viewport={{ once: true }}
            >
              <span>Featured</span>
            </motion.div>

            {/* Main content - asymmetric */}
            <div className="featured-content">
              <motion.div
                className="featured-image-wrapper"
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <div className="image-frame">
                  <img src="/images/mini-golf/Main-Hero-Image.png" alt="Murder! In Wonderland" />
                </div>
                <div className="image-decoration"></div>
              </motion.div>

              <motion.div
                className="featured-text"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <h2 className="featured-title">
                  Murder!<br/>
                  <span className="title-italic">In Wonderland</span>
                </h2>
                <p className="featured-description">
                  A 9-hole Alice in Wonderland themed mini golf murder mystery
                  featuring a <strong>22-foot castle</strong> centerpiece.
                </p>
                <p className="featured-credit">
                  Led with mentorship from Disney Imagineer<br/>
                  <em>Joe Garlington</em>
                </p>
                <Link to="/experiential/immersive" className="featured-link">
                  See the experience <span>→</span>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Concept - Olivia Rodrigo */}
      <section className="featured-concept-home">
        <div className="container">
          <div className="featured-concept-layout">
            <div className="featured-concept-content">
              <motion.div
                className="featured-concept-text"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <motion.span
                  className="concept-inline-label"
                  initial={{ opacity: 0, rotate: -5 }}
                  whileInView={{ opacity: 1, rotate: -3 }}
                  viewport={{ once: true }}
                >
                  Concept
                </motion.span>
                <span className="concept-tag-small">Fan Activation</span>
                <h2 className="featured-concept-title">
                  &ldquo;Drivers License:<br/>
                  <span className="title-italic">The Anniversary Experience&rdquo;</span>
                </h2>
                <p className="featured-concept-description">
                  A speculative immersive pop-up concept celebrating Olivia Rodrigo's
                  breakthrough single. Three distinct rooms translating nostalgia, heartbreak,
                  and coming-of-age into explorable art.
                </p>
                <Link to="/experiential/concepts/olivia-rodrigo" className="featured-link">
                  View the concept <span>→</span>
                </Link>
              </motion.div>

              <motion.div
                className="featured-concept-image-wrapper"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <div className="concept-image-frame">
                  <img
                    src="/images/concepts/olivia-rodrigo/room1/Mockup-RoomOverview.png"
                    alt="Drivers License immersive experience mockup"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Creative Editorial Layout */}
      <section className="about-section-creative">
        <div className="container">
          <div className="about-creative-layout">
            {/* Big stacked typography - roles */}
            <motion.div
              className="roles-stack"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <motion.span
                className="about-tag-inline"
                initial={{ opacity: 0, rotate: -5 }}
                whileInView={{ opacity: 1, rotate: -3 }}
                viewport={{ once: true }}
              >
                About
              </motion.span>
              <span className="role role-1">Maker</span>
              <span className="role role-2">Designer</span>
              <span className="role role-3">Storyteller</span>
            </motion.div>

            {/* Content column */}
            <div className="about-content-column">
              <motion.p
                className="about-lead"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                Graduated from the <strong>University of Southern California's Iovine and Young Academy</strong>
                {' '}with a B.S. focused in Design, Technology, and Business, alongside a
                <strong> Minor in Applications of AI</strong>. Passionate about shaping experiences across physical & digital spaces.
              </motion.p>

              {/* Fun facts */}
              <div className="credential-scraps">
                <motion.div
                  className="scrap"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <span className="scrap-number">USC</span>
                  <span className="scrap-label">'24 Grad</span>
                </motion.div>
                <motion.div
                  className="scrap"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <span className="scrap-icon">✦</span>
                  <span className="scrap-label">AI Minor</span>
                </motion.div>
                <motion.div
                  className="scrap"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                >
                  <span className="scrap-icon">♫</span>
                  <span className="scrap-label">Music Fan</span>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
              >
                <Link to="/experiential/about" className="about-link">
                  Learn more about me <span>→</span>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - More Playful */}
      <section className="cta-section-playful">
        <div className="container">
          <motion.div
            className="cta-box"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2>
              Let's create something<br/>
              <em>unforgettable</em>
            </h2>
            <p>
              Seeking opportunities creating fan experiences, immersive activations,
              themed attractions, and live events where superfans feel like they've
              stepped into the worlds they love.
            </p>
            <div className="cta-buttons">
              <a href="mailto:sami@samidenicola.com" className="btn btn-primary">
                Get in Touch
              </a>
              <Link to="/experiential/about" className="btn btn-outline">
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}

export default Home;
